import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import { TooltipProvider } from './components/ui/tooltip';
import Navigation from './components/Navigation';
import { Suspense, lazy } from 'react';
import { LoadingState } from './components/ui/loading-state';
import { Permission, Permissions } from '@shiftly/shared/src/permissions';
import TermsAndConditions from './components/views/TermsAndConditions';
import PrivacyPolicy from './components/views/PrivacyPolicy';
import TermsGuard from './components/views/guards/TermsGuard';
import NameGuard from './components/views/guards/NameGuard';
import TaxCountryGuard from './components/views/guards/TaxCountryGuard';
import TrialGuard from './components/views/guards/TrialGuard';
import Footer from './components/Footer';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import TicketCategoryManagersView from './components/views/TicketCategoryManagersView';
import PaymentFilesManager from './components/views/PaymentFiles';
import ShiftAveragesView from './components/views/ShiftAveragesView';
import AuditLogsView from './components/views/AuditLogsView';
import ModelScheduleView from './components/views/ModelScheduleView';
import TimeOffView from './components/views/TimeOffView';
import OnlyFansTerms from './components/views/OnlyFansTerms';
import { CostsView } from './components/views/costsview';
import { RoleEmulator } from './components/RoleEmulator';
import ModelEarningsBreakdownView from './components/views/ModelEarningsBreakdownView';
import { RealtimeShiftProvider } from './contexts/RealtimeShiftContext';
import { RealtimeScheduleShiftProvider } from './contexts/RealtimeScheduleShiftContext';
import { RealtimeSpamShiftProvider } from './contexts/RealtimeSpamShiftContext';
import { RealtimeNotificationProvider } from './contexts/RealtimeNotificationContext';
import { RealtimeSupervisorShiftProvider } from './contexts/RealtimeSupervisorShiftContext';
import { RealtimeGGSwapShiftProvider } from './contexts/RealtimeGGSwapShiftContext';

// Lazy load route components
const Login = lazy(() => import('./components/views/Login'));
const Dashboard = lazy(() => import('./components/views/Dashboard'));
const UserView = lazy(() => import('./components/views/UserViewWrapper'));
const ModelsView = lazy(() => import('./components/views/ModelsView'));
const FormatListView = lazy(() => import('./components/views/FormatListView'));
const ModelView = lazy(() => import('./components/views/ModelView'));
const UsersManagementView = lazy(() => import('./components/views/UsersManagementView'));
const VerificationView = lazy(() => import('./components/views/VerificationView'));
const RotaView = lazy(() => import('./components/views/RotaView'));
const GGSwapRotaView = lazy(() => import('./components/views/GGSwapRotaView'));
const NotFoundView = lazy(() => import('./components/views/NotFoundView'));
const ExchangeRatesView = lazy(() => import('./components/views/ExchangeRatesView'));
const UserPayBreakdownView = lazy(() => import('./components/views/userpaybreakdownview/UserPayBreakdownView'));
const RolesManagementView = lazy(() => import('./components/views/RolesManagementView'));
const NotificationsView = lazy(() => import('./components/views/NotificationsView'));
const AcceptTerms = lazy(() => import('./components/views/AcceptTerms'));
const HolidayView = lazy(() => import('./components/views/HolidayView'));
const OverwatchRequestsView = lazy(() => import('./components/views/OverwatchRequestsView'));
const OvertimeManagementView = lazy(() => import('./components/views/OvertimeManagementView'));
const Overtime = lazy(() => import('./components/views/Overtime'));
const RequestsView = lazy(() => import('./components/views/supportsystemview/RequestsView'));
const ScheduleRotaView = lazy(() => import('./components/views/ScheduleRotaView'));
const SpamShiftRotaView = lazy(() => import('./components/views/SpamShiftRotaView'));
const SupervisorShiftRotaView = lazy(() => import('./components/views/SupervisorShiftRotaView'));
const PayExceptionsView = lazy(() => import('./components/views/PayExceptionsView'));
const TicketView = lazy(() => import('./components/views/ticketview/index'));
const WeekendStaffingView = lazy(() => import('./components/views/WeekendStaffingView'));
const HoursValidationManager = lazy(() => import('./components/views/HoursValidationManager'));
const MiscPaymentsView = lazy(() => import('./components/views/MiscPaymentsView'));
const ReferralsView = lazy(() => import('./components/views/referrals/index'));
const InvoiceHelper = lazy(() => import('./components/views/userview/InvoiceHelper'));
const BankingDetailsView = lazy(() => import('./components/views/BankingDetailsView'));
const InvoiceSubmittedView = lazy(() => import('./components/views/InvoiceSubmittedView'));
const PromotionAnalysisView = lazy(() => import('./components/views/PromotionAnalysisView'));
const DiscordCallback = lazy(() => import('./components/views/DiscordCallback'));
const PasswordReset = lazy(() => import('./components/views/PasswordReset'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Disable automatic refetch on window focus
      refetchOnReconnect: 'always',
    },
    mutations: {
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Protected Route wrapper component
const ProtectedRoute = ({
  children,
  requiredRoles
}: {
  children: React.ReactNode;
  requiredRoles?: Permission[];
}) => {
  const { user, permissions, loading: authLoading } = useAuth();
  const location = useLocation();

  // Wait for both auth and user data to load
  if (authLoading) {
    return <LoadingState />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Now we can safely check the role since userData has loaded
  if (requiredRoles && !requiredRoles.some(r => permissions.includes(r))) {
    return <Navigate to="/" replace />;
  }

  return (
    <TrialGuard>
      <TermsGuard>
        <NameGuard>
          <TaxCountryGuard>
            {children}
          </TaxCountryGuard>
        </NameGuard>
      </TermsGuard>
    </TrialGuard>
  );
};

// Auth Route wrapper component for login/register pages
const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();

  if (authLoading) {
    return <LoadingState />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PaymentFilesRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { type } = useParams<{ type: string }>();
  const { permissions } = useAuth();

  if (type) {
    const typePermissionMap: Record<string, Permission[]> = {
      'pay-summary': [Permissions.SHEETS_PAY_SUMMARY],
      'model-pay-summary': [Permissions.SHEETS_MODEL_PAY_SUMMARY],
      'invoices': [Permissions.SHEETS_INVOICES],
      'model-ppv': [Permissions.SHEETS_MODEL_PPV],
      'staff-report': [Permissions.SHEETS_STAFF_REPORT],
      'model-earnings-report': [Permissions.SHEETS_MODEL_EARNINGS_REPORT],
      'model-statistics': [Permissions.SHEETS_MODEL_STATISTICS],
      'fan-gain-gg-swaps': [Permissions.SHEETS_FAN_GAIN],
      'fan-gain-groups': [Permissions.SHEETS_FAN_GAIN],
      'fan-gain-totals': [Permissions.SHEETS_FAN_GAIN],
      'user-model-assignment': [Permissions.SHEETS_FAN_GAIN],
      'fan-gain-per-ad': [Permissions.SHEETS_FAN_GAIN_PER_AD],
    };

    const requiredPermissions = typePermissionMap[type];
    if (requiredPermissions && !requiredPermissions.some(p => permissions.includes(p))) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

const AppContent = () => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isTermsPage = location.pathname === '/terms' || location.pathname === '/privacy';
  const isPublicPage = location.pathname.startsWith('/banking-details/') || location.pathname === '/invoice-submitted';

  return (
    <>
      {(!isAuthPage && !isTermsPage && !isPublicPage) && <Navigation />}
      <div className="flex min-h-screen flex-col overflow-x-hidden">
        <div className="relative flex-1 bg-gradient-to-br from-brand-background via-brand-background/95 to-brand-light/5 dark:from-brand-background dark:via-brand-background/95 dark:to-brand-light/3">
          <div className="pointer-events-none fixed inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
          <div className="pointer-events-none fixed left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-brand-gold opacity-20 blur-[100px] dark:opacity-10" />
          <div className="pointer-events-none fixed right-[-20%] top-[-20%] -z-10 h-[500px] w-[500px] rounded-full bg-brand-pink opacity-20 blur-[100px] dark:opacity-10" />
          <div className="container relative mx-auto px-4 py-8">
            <ChunkErrorBoundary>
              <Suspense fallback={<LoadingState />}>
                <Routes>
                  <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
                  <Route path="/password-reset" element={<PasswordReset />} />
                  <Route
                    path="/terms"
                    element={
                      <TermsAndConditions />
                    }
                  />
                  <Route
                    path="/privacy"
                    element={
                      <PrivacyPolicy />
                    }
                  />
                  {/* Unauth banking details routes for deactivated users */}
                  <Route
                    path="/banking-details/:tokenId"
                    element={<BankingDetailsView />}
                  />
                  <Route
                    path="/invoice-submitted"
                    element={<InvoiceSubmittedView />}
                  />
                  <Route
                    path="/discord-callback"
                    element={<DiscordCallback />}
                  />
                  <Route
                    path="/onlyfans-terms"
                    element={
                      <ProtectedRoute>
                        <OnlyFansTerms />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/accept-terms"
                    element={
                      <ProtectedRoute>
                        <AcceptTerms />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <UserView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoice-helper"
                    element={
                      <ProtectedRoute>
                        <InvoiceHelper />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/users/:user_id"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_USERS]}>
                        <UserView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users/verification"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_USERS]}>
                        <VerificationView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users/:user_id/pay-breakdown"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.PAY_VIEW_OWN]}>
                        <UserPayBreakdownView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/models"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_MODELS]}>
                        <ModelsView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/models/:id"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_MODELS]}>
                        <ModelView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/models/:model_id"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_MODELS]}>
                        <ModelView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/models/:id/schedule"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.SCHEDULE_EDIT, Permissions.SCHEDULE_EDIT_RESET_TIMER]}>
                        <ModelScheduleView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/models/:model_id/earnings-breakdown"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_MODELS]}>
                        <ModelEarningsBreakdownView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/format-list"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.SCHEDULE_VIEW_FORMAT_LIST]}>
                        <FormatListView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_USERS]}>
                        <UsersManagementView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rota"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_ROTA]}>
                        <RotaView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gg-swap-rota"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.GG_SWAP_MANAGE]}>
                        <GGSwapRotaView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/exchange-rates"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_EXCHANGE_RATES]}>
                        <ExchangeRatesView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/roles"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_ROLES]}>
                        <RolesManagementView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <NotificationsView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/holidays"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.HOLIDAYS_VIEW]}>
                        <HolidayView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/overwatch-requests"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.REQUESTS_VIEW_OVERWATCH]}>
                        <OverwatchRequestsView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/overtime-management"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.OVERTIME_MANAGE]}>
                        <OvertimeManagementView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/overtime"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.OVERTIME_REQUEST]}>
                        <Overtime />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/tickets" element={
                    <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_TICKETS]}>
                      <TicketView />
                    </ProtectedRoute>
                  } />
                  <Route path="/feedback" element={
                    <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_SUPPORT]}>
                      <RequestsView />
                    </ProtectedRoute>
                  } />
                  <Route
                    path="/ticket-category-managers"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.TICKETS_CATEGORY_MANAGERS]}>
                        <TicketCategoryManagersView />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/scheduling-rota" element={
                    <ProtectedRoute requiredRoles={[Permissions.SCHEDULE_EDIT]}>
                      <ScheduleRotaView />
                    </ProtectedRoute>
                  } />
                  <Route path="/spam-shift-rota" element={
                    <ProtectedRoute requiredRoles={[Permissions.SPAM_SHIFTS_MANAGE]}>
                      <SpamShiftRotaView />
                    </ProtectedRoute>
                  } />
                  <Route path="/supervisor-rota" element={
                    <ProtectedRoute requiredRoles={[Permissions.SUPERVISOR_SHIFTS_MANAGE]}>
                      <SupervisorShiftRotaView />
                    </ProtectedRoute>
                  } />
                  <Route path="/pay-exceptions" element={
                    <ProtectedRoute requiredRoles={[Permissions.SCHEDULE_EDIT]}>
                      <PayExceptionsView />
                    </ProtectedRoute>
                  } />
                  <Route path="/payment-files" element={
                    <ProtectedRoute requiredRoles={[
                      Permissions.SHEETS_PAY_SUMMARY,
                      Permissions.SHEETS_MODEL_PAY_SUMMARY,
                      Permissions.SHEETS_INVOICES,
                      Permissions.SHEETS_MODEL_PPV,
                      Permissions.SHEETS_STAFF_REPORT,
                      Permissions.SHEETS_MODEL_EARNINGS_REPORT,
                      Permissions.SHEETS_MODEL_STATISTICS,
                      Permissions.SHEETS_FAN_GAIN,
                      Permissions.SHEETS_FAN_GAIN_PER_AD,
                    ]}>
                      <PaymentFilesRouteGuard>
                        <PaymentFilesManager />
                      </PaymentFilesRouteGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/payment-files/:year/:month/:type" element={
                    <ProtectedRoute requiredRoles={[
                      Permissions.SHEETS_PAY_SUMMARY,
                      Permissions.SHEETS_MODEL_PAY_SUMMARY,
                      Permissions.SHEETS_INVOICES,
                      Permissions.SHEETS_MODEL_PPV,
                      Permissions.SHEETS_STAFF_REPORT,
                      Permissions.SHEETS_MODEL_EARNINGS_REPORT,
                      Permissions.SHEETS_MODEL_STATISTICS,
                      Permissions.SHEETS_FAN_GAIN,
                      Permissions.SHEETS_FAN_GAIN_PER_AD,
                    ]}>
                      <PaymentFilesRouteGuard>
                        <PaymentFilesManager />
                      </PaymentFilesRouteGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/payment-files/staff-report" element={
                    <ProtectedRoute requiredRoles={[Permissions.SHEETS_STAFF_REPORT]}>
                      <PaymentFilesManager />
                    </ProtectedRoute>
                  } />
                  <Route path="/payment-files/model-earnings-report" element={
                    <ProtectedRoute requiredRoles={[Permissions.SHEETS_MODEL_EARNINGS_REPORT]}>
                      <PaymentFilesManager />
                    </ProtectedRoute>
                  } />
                  <Route path="/payment-files/fan-gain-per-ad" element={
                    <ProtectedRoute requiredRoles={[Permissions.SHEETS_FAN_GAIN_PER_AD]}>
                      <PaymentFilesManager />
                    </ProtectedRoute>
                  } />
                  <Route
                    path="/weekend-staffing"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_WEEKEND_STAFFING]}>
                        <WeekendStaffingView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shift-averages"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_ROTA]}>
                        <ShiftAveragesView />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/hours-validation" element={
                    <ProtectedRoute requiredRoles={[Permissions.PAY_VIEW_OTHERS]}>
                      <HoursValidationManager />
                    </ProtectedRoute>
                  }
                  />
                  <Route
                    path="/time-off"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.HOLIDAYS_MANAGE]}>
                        <TimeOffView />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/audit-logs" element={
                    <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_AUDIT_LOGS]}>
                      <AuditLogsView />
                    </ProtectedRoute>
                  } />
                  <Route path="/promo-distribution" element={
                    <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_PROMO_DISTRIBUTION]}>
                      <PromotionAnalysisView />
                    </ProtectedRoute>
                  } />
                  <Route path="/misc-payments" element={
                    <ProtectedRoute requiredRoles={[Permissions.PAY_MISC_PAYMENTS]}>
                      <MiscPaymentsView />
                    </ProtectedRoute>
                  } />
                  <Route path="/referrals" element={
                    <ProtectedRoute requiredRoles={[Permissions.PAY_REFERRALS]}>
                      <ReferralsView />
                    </ProtectedRoute>
                  } />
                  <Route
                    path="/costs"
                    element={
                      <ProtectedRoute requiredRoles={[Permissions.NAVIGATION_COSTS]}>
                        <CostsView />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundView />} />
                </Routes>
              </Suspense>
            </ChunkErrorBoundary>
          </div>
        </div>
        <Footer />
        <Toaster
          position="bottom-right"
          toastOptions={{
            className: '',
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--card-foreground))',
              border: '1px solid hsl(var(--border))',
              padding: '16px',
              borderRadius: 'var(--radius)',
            },
            success: {
              className: '',
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
                border: '1px solid hsl(var(--border))',
              },
              iconTheme: {
                primary: 'hsl(var(--primary))',
                secondary: 'hsl(var(--primary-foreground))',
              },
            },
            error: {
              className: '',
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
                border: '1px solid hsl(var(--destructive))',
              },
              iconTheme: {
                primary: 'hsl(var(--destructive))',
                secondary: 'hsl(var(--destructive-foreground))',
              },
            },
            loading: {
              className: '',
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
                border: '1px solid hsl(var(--border))',
              },
            },
          }}
        />
        <RoleEmulator />
      </div>
    </>
  );
};

const App = (): JSX.Element => {
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <RealtimeNotificationProvider>
              <RealtimeSupervisorShiftProvider>
                <RealtimeShiftProvider>
                  <RealtimeScheduleShiftProvider>
                    <RealtimeSpamShiftProvider>
                      <RealtimeGGSwapShiftProvider>
                        <AppContent />
                      </RealtimeGGSwapShiftProvider>
                    </RealtimeSpamShiftProvider>
                  </RealtimeScheduleShiftProvider>
                </RealtimeShiftProvider>
              </RealtimeSupervisorShiftProvider>
            </RealtimeNotificationProvider>
          </AuthProvider>
        </TooltipProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Router>
  );
};

export default App